import { useContext } from 'react';
import AuthContext from '../../context/authProvider';
import { usePrivileges } from './usePermissions';

// Custom hook for components to use
export function useLicenseUtils() {
    const {permissions, license_plan, license_features, module_access, license_status} = usePrivileges();
    
    return {
        canAccessFeature: (featureKey) => {
            if (!license_features || !Array.isArray(license_features)) {
                return false;
            }
            const feature = license_features.find(f => f.key === featureKey);
            return feature?.enabled || false;
        },
        
        getFeatureConfig: (featureKey) => {
            if (!license_features || !Array.isArray(license_features)) {
                return null;
            }
            return license_features.find(f => f.key === featureKey) || null;
        },
        
        getFeatureLimit: (featureKey, limitKey) => {
            const feature = getFeatureConfig(featureKey);
            return feature?.limits?.[limitKey] || null;
        },
        getLicenseStatus: () => {
            return license_status;
        },
        
        hasModuleAccess: (moduleKey) => {
            if (!module_access || !Array.isArray(module_access)) {
                return false;
            }
            const module = module_access.find(m => m.key === moduleKey);
            return module?.enabled || false;
        },
        
        canPerformAction: (moduleKey, action) => {
            if (!module_access || !Array.isArray(module_access)) {
                return false;
            }
            const module = module_access.find(m => m.key === moduleKey);
            return module?.permissions?.includes(action) || false;
        },
        
        getEnabledFeatures: () => {
            if (!license_features || !Array.isArray(license_features)) {
                return [];
            }
            return license_features.filter(f => f.enabled).map(f => f.key);
        },
        
        getAccessibleModules: () => {
            if (!module_access || !Array.isArray(module_access)) {
                return [];
            }
            return module_access.filter(m => m.enabled).map(m => m.key);
        },
        
        validateFeatureUsage: (featureKey, limitKey, currentUsage) => {
            const limit = getFeatureLimit(featureKey, limitKey);
            if (limit === null || limit === undefined) {
                return true; // No limit set
            }
            return currentUsage < limit;
        },
        
        hasPermission: (permission) => {
            if (!permissions) {
                return false;
            }
            return permissions.includes(permission);
        },
        
        getMainLicensePlan: () => {
            if (!license_plan) {
                return null;
            }
            // Ensure license_plan is always treated as an array
            const licensePlans = Array.isArray(license_plan) 
                ? license_plan 
                : (license_plan ? [license_plan] : []);
            const mainPlans = ['starter', 'standard', 'enterprise'];
            return licensePlans.find(plan => mainPlans.includes(plan)) || null;
        },
        
        hasAddon: (addonName) => {
            if (!license_plan) {
                return false;
            }
            // Ensure license_plan is always treated as an array
            const licensePlans = Array.isArray(license_plan) 
                ? license_plan 
                : (license_plan ? [license_plan] : []);
            return licensePlans.includes(addonName);
        },
        
        // Raw data access
        permissions, 
        license_plan,
        license_features,
        module_access,
        license_status
    };
}

// Utility functions that accept privileges as parameter (for non-component usage)
export function getMainLicensePlan(privileges) {
    if (!privileges || !privileges.license_plan) {
        return null;
    }
    // Ensure license_plan is always treated as an array
    const licensePlans = Array.isArray(privileges.license_plan) 
        ? privileges.license_plan 
        : (privileges.license_plan ? [privileges.license_plan] : []);
    const mainPlans = ['starter', 'standard', 'enterprise'];
    return licensePlans.find(plan => mainPlans.includes(plan)) || null;
}

export function hasAddon(addonName, privileges) {
    if (!privileges || !privileges.license_plan) {
        return false;
    }
    // Ensure license_plan is always treated as an array
    const licensePlans = Array.isArray(privileges.license_plan) 
        ? privileges.license_plan 
        : (privileges.license_plan ? [privileges.license_plan] : []);
    return licensePlans.includes(addonName);
}

export function getAddons(privileges) {
    if (!privileges || !privileges.license_plan) {
        return [];
    }
    
    // Ensure license_plan is always treated as an array
    const licensePlans = Array.isArray(privileges.license_plan) 
        ? privileges.license_plan 
        : (privileges.license_plan ? [privileges.license_plan] : []);
    const mainPlans = ['starter', 'standard', 'enterprise'];
    
    return licensePlans.filter(plan => !mainPlans.includes(plan));
}

export function hasEnterpriseOrAddon(addonName, privileges) {
    const mainPlan = getMainLicensePlan(privileges);
    return mainPlan === 'enterprise' || hasAddon(addonName, privileges);
}

export function getPlanInfo(privileges) {
    const mainPlan = getMainLicensePlan(privileges);
    const addons = getAddons(privileges);
    
    return {
        mainPlan,
        addons,
        allPlans: privileges?.license_plan || [],
        hasEnterprise: mainPlan === 'enterprise',
        hasStandard: mainPlan === 'standard' || mainPlan === 'enterprise',
        hasStarter: mainPlan === 'starter' || mainPlan === 'standard' || mainPlan === 'enterprise'
    };
}

// Core utility functions that accept privileges as parameter
export function canAccessFeature(featureKey, privileges) {
    if (!privileges?.license_features || !Array.isArray(privileges.license_features)) {
        return false;
    }
    const feature = privileges.license_features.find(f => f.key === featureKey);
    return feature?.enabled || false;
}

export function getFeatureConfig(featureKey, privileges) {
    if (!privileges?.license_features || !Array.isArray(privileges.license_features)) {
        return null;
    }
    return privileges.license_features.find(f => f.key === featureKey) || null;
}

export function getFeatureLimit(featureKey, limitKey, privileges) {
    const feature = getFeatureConfig(featureKey, privileges);
    return feature?.limits?.[limitKey] || null;
}

export function hasModuleAccess(moduleKey, privileges) {
    if (!privileges?.module_access || !Array.isArray(privileges.module_access)) {
        return false;
    }
    const module = privileges.module_access.find(m => m.key === moduleKey);
    return module?.enabled || false;
}

export function canPerformAction(moduleKey, action, privileges) {
    if (!privileges?.module_access || !Array.isArray(privileges.module_access)) {
        return false;
    }
    const module = privileges.module_access.find(m => m.key === moduleKey);
    return module?.permissions?.includes(action) || false;
}

export function getEnabledFeatures(privileges) {
    if (!privileges?.license_features || !Array.isArray(privileges.license_features)) {
        return [];
    }
    return privileges.license_features.filter(f => f.enabled).map(f => f.key);
}

export function getAccessibleModules(privileges) {
    if (!privileges?.module_access || !Array.isArray(privileges.module_access)) {
        return [];
    }
    return privileges.module_access.filter(m => m.enabled).map(m => m.key);
}

export function validateFeatureUsage(featureKey, limitKey, currentUsage, privileges) {
    const limit = getFeatureLimit(featureKey, limitKey, privileges);
    if (limit === null || limit === undefined) {
        return true; // No limit set
    }
    return currentUsage < limit;
}

// Non-hook permission check for usage outside React components (e.g., sagas/services)
export function hasPermission(permission, privileges) {
    if (!privileges || !Array.isArray(privileges.permissions)) {
        return false;
    }
    return privileges.permissions.includes(permission);
}